// Ephemeral speech bubble above a target {x,y}. Self-follows each frame, auto-removes
// after ~16s (4× the old 4s) — or sooner when a new message replaces it (callers destroy
// the previous bubble before creating the next one).
import { UI_DEPTH } from './ySort.js';
import { iconText } from './iconText.js';
import { headTop } from './poseLayers.mjs';

const BUBBLE_LIFETIME_MS = 16000;

// Vertical gap above the target's y. A seated NatalkaSprite's y is the sit line (visible
// bottom of the cropped body, head top ~90px above it), so the standing offset (180) floats
// the bubble ~80-90px too high above a seated head — use a shorter offset when seated.
// Non-NatalkaSprite targets (e.g. GameHallTables' plain {x,y} points) have no `seated`
// getter, so `target.seated` reads undefined there and falls through to the standing offset.
const BUBBLE_OFFSET_STANDING = 180;
const BUBBLE_OFFSET_SEATED = 110;

// …and on the outdoor scenes the character herself is drawn smaller (worldScale), so the
// gap above her head has to shrink with her or the bubble floats off on its own.
// On a sofa / in a bed (target.pose) the head is wherever the pose puts it: the same gap above that.
const BUBBLE_ABOVE_HEAD = BUBBLE_OFFSET_STANDING - 120;
function bubbleOffset(target) {
  const base = target?.pose ? BUBBLE_ABOVE_HEAD - headTop(target.pose).y
    : target?.seated ? BUBBLE_OFFSET_SEATED : BUBBLE_OFFSET_STANDING;
  return base * (target?.worldScale ?? 1);
}

export class ChatBubble {
  constructor(scene, target, text) {
    this.scene = scene;
    this.target = target;
    this.label = iconText(scene, target.x, target.y - bubbleOffset(target), text, {
      fontFamily: 'system-ui', fontSize: '18px', color: '#222',
      backgroundColor: '#fff', padding: { x: 10, y: 6 }, wordWrap: { width: 260 },
    }).setOrigin(0.5, 1).setDepth(UI_DEPTH - 1);
    // A target inside a panned / scaled world container (the farm's iso world): its x/y are that container's local
    // coordinates, so the bubble has to live in the same container — else it floats off by the pan offset, drawn at
    // the wrong scale (the local player's bubble on the farm, 2026-10-03; the farm re-parents peers' bubbles itself).
    this._adopt = () => {
      const c = this.target?.parentContainer;
      if (c && this.label && this.label.parentContainer !== c) { const d = this.label.depth; c.add(this.label); this.label.setDepth(d); }
    };
    this._adopt();
    this._follow = () => { if (this.label && this.target) { this._adopt(); this.label.setPosition(this.target.x, this.target.y - bubbleOffset(this.target)); } };
    scene.events.on('update', this._follow);
    this._timer = scene.time.delayedCall(BUBBLE_LIFETIME_MS, () => this.destroy());
  }
  destroy() {
    this.scene?.events?.off('update', this._follow);
    this.label?.destroy(); this.label = null;
    this._timer?.remove?.(); this._timer = null;
  }
}
